import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  IconButton,
} from '@mui/material';
import { ArrowBack, ArrowForward, Close } from '@mui/icons-material';
import { TaskSelector } from '../upload/TaskSelector';
import { useAnalysisContext } from '../../context/AnalysisContext';
import { useAuth } from '../../context/AuthContext';
import { chatService } from '../../services/chatService';
import { DeckSummary } from '../../types';

interface ReanalysisDialogProps {
  open: boolean;
  onClose: () => void;
  deck: DeckSummary | null;
  onSuccess?: () => void;
}

const steps = ['Choose Analysis Tasks', 'Confirm & Re-analyze'];

export const ReanalysisDialog: React.FC<ReanalysisDialogProps> = ({
  open,
  onClose,
  deck,
  onSuccess,
}) => {
  const { token } = useAuth();
  const { 
    selectedTasks, 
    promptOverrides, 
    loadTasks, 
    clearPromptOverrides, 
    loadDefaultPrompts 
  } = useAnalysisContext();
  
  const [activeStep, setActiveStep] = useState(0);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Load tasks and defaults when dialog opens
  useEffect(() => {
    if (open) {
      clearPromptOverrides();
      loadTasks();
      loadDefaultPrompts();
      setActiveStep(0);
      setError(undefined);
      setReanalyzing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleReanalyze();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReanalyze = async () => {
    if (!deck || !token) return;

    setReanalyzing(true);
    setError(undefined);

    try {
      // Generate slide numbers array if totalSlides is available
      const slides = deck.totalSlides 
        ? Array.from({ length: deck.totalSlides }, (_, i) => i + 1)
        : [];
      
      // Pass selected tasks to backend
      await chatService.requestReanalysis(deck.deckId, slides, selectedTasks, token);
      
      // Close dialog
      onClose();
      
      // Call success callback to refresh data
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Failed to re-analyze deck:', err);
      setError((err as Error).message || 'Failed to start re-analysis. Please try again.');
    } finally {
      setReanalyzing(false);
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedTasks.length > 0;
      case 1:
        return selectedTasks.length > 0 && !reanalyzing;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <TaskSelector />;
      case 1:
        return (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Ready to Re-analyze
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                File: {deck?.fileName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Slides: {deck?.totalSlides ?? 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tasks: {selectedTasks.length} selected
              </Typography>
            </Paper>
            {error && (
              <Paper sx={{ p: 2, bgcolor: 'error.light', mb: 2 }}>
                <Typography variant="body2" color="error.contrastText">
                  {error}
                </Typography>
              </Paper>
            )}
            {reanalyzing && (
              <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
                <Typography variant="body2" color="info.contrastText">
                  Starting re-analysis...
                </Typography>
              </Paper>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={reanalyzing ? undefined : onClose} 
      maxWidth="md" 
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle onClick={(e) => e.stopPropagation()}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Re-analyze Presentation</Typography>
          <IconButton 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            disabled={reanalyzing}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Select analysis parameters for re-analyzing {deck?.fileName}
        </Typography>
      </DialogTitle>
      
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {renderStepContent()}

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Button
            type="button"
            disabled={activeStep === 0 || reanalyzing}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleBack();
            }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>

          <Button
            type="button"
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleNext();
            }}
            disabled={!canProceed()}
            endIcon={activeStep === steps.length - 1 ? undefined : <ArrowForward />}
          >
            {activeStep === steps.length - 1 ? 'Re-analyze' : 'Next'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

